import { DAYS, TIME_SLOTS, COURTS } from "./tiers";
import { getOccupiedSlots } from "./time";

function buildBusySet(groups) {
  const busy = new Set();
  groups.forEach(g => {
    if (!g.day || !g.court) return;
    getOccupiedSlots(g).forEach(slot => busy.add(`${g.day}|${slot}|${g.court}`));
  });
  return busy;
}

function majorityTier(players) {
  const counts = {};
  players.forEach(p => { counts[p.tier] = (counts[p.tier] || 0) + 1; });
  let best = null, bestCount = -1;
  Object.entries(counts).forEach(([tier, count]) => {
    if (count > bestCount) { best = tier; bestCount = count; }
  });
  return best;
}

/**
 * For a set of selected players, finds every Day×TIME_SLOTS combo where at
 * least 2 of them share availability, ranked by how many match (highest
 * first, then earliest day/time). Each result also reports which courts are
 * free at that time and which existing groups (with room) already meet then,
 * so a match can be acted on directly rather than just displayed.
 */
export function computeMatches(selectedPlayers, groups) {
  if (selectedPlayers.length < 2) return [];
  const busy = buildBusySet(groups);

  const results = [];
  DAYS.forEach(day => {
    TIME_SLOTS.forEach(slot => {
      const key = `${day}|${slot}`;
      const matched = selectedPlayers.filter(p => p.availability.includes(key));
      if (matched.length < 2) return;
      const missing = selectedPlayers.filter(p => !matched.includes(p));

      const openCourts = COURTS.filter(court => !busy.has(`${day}|${slot}|${court}`));

      const joinableGroups = groups
        .filter(g => {
          if (g.day !== day || !g.court) return false;
          if (g.playerIds.length >= 4) return false;
          if (!getOccupiedSlots(g).includes(slot)) return false;
          return matched.some(p => !g.playerIds.includes(p.id));
        })
        .map(g => ({
          group: g,
          joiningPlayers: matched.filter(p => !g.playerIds.includes(p.id)),
        }));

      results.push({
        key: `${day}|${slot}`,
        day,
        slot,
        matchCount: matched.length,
        matched,
        missing,
        openCourts,
        joinableGroups,
        suggestedTier: majorityTier(matched),
      });
    });
  });

  results.sort((a, b) => {
    if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
    const dayDiff = DAYS.indexOf(a.day) - DAYS.indexOf(b.day);
    if (dayDiff !== 0) return dayDiff;
    return TIME_SLOTS.indexOf(a.slot) - TIME_SLOTS.indexOf(b.slot);
  });

  return results;
}
